import '../globals/public-path';
import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import {createStore} from 'redux';
import rootReducer from 'reducers/index';
import ListingInfo from '../components/listing-info/listing-info';
import page from 'helpers/page';
import PreloadedSection from '../components/helpers/preloaded-section/preloaded-section';
import {setLoyaltyBurn, setMedia} from '../actions/actions';
import egtnlUtil from '../helpers/egtnl-util';
import appOptions from '../helpers/appOptions';
import loyaltyHelper from '../helpers/loyalty';
import picketlineAnalytics from '../helpers/picketline-analytics';
import CheckoutModel from './modules/model/checkout';
import './modules/subcomponents/floating-label/floating-label';
import egtnlWrapper from '../helpers/egtnlWeb';
import './stylesheets/_tc.less';
import url from '../helpers/url';
import initApollo from '../apollo/apollo-init';

// initialize appOptions
appOptions.initializeForRequest();

// Trigger EDAP event before service calls
picketlineAnalytics.checkoutInitiated();

// initialize egtnl
egtnlWrapper.initialiseEgtnlBucket(
    window.__INITIAL_STATE__.clientEvaluationDatas
);

if (egtnlUtil.isSeparateMediaCallEnabled()) {
    const ListingInfoWrapper = ({onRender}) => {
        const ContactDetailsStepRef = useRef(null);
        const RulesStepTwoRef = useRef(null);
        const PrimaryActionRenderedPromiseRef = useRef(null);
        const [mediaState, setMediaState] = useState(null);
        const [checkoutModelState, setCheckoutModelState] = useState(null);

        if (!page.dataStorage.store) {
            page.initStore(createStore(rootReducer));
        }
        const store = page.dataStorage.store;

        useEffect(() => {
            const swpApplied = url.getUrlParameter('swpApplied')
                ? url.getUrlParameter('swpApplied') === 'true'
                : true;
            store.dispatch(setLoyaltyBurn(swpApplied));

            if (window.__INITIAL_STATE__.triggerInlineAjax) {
                // Fetch data from /createCheckoutv3Perf endpoint
                window.TC.checkoutModelFetch.then((data) => {
                    window.TC.checkoutModel = new CheckoutModel();
                    const checkoutModel = window.TC.checkoutModel;
                    const parsedData = checkoutModel.parse(data);
                    checkoutModel.set(parsedData);
                    setCheckoutModelState(checkoutModel);

                    // update store initial state after the model data is in
                    if (checkoutModel && store) {
                        // update loyalty burn initial state
                        // TODO: encapsulate better if the need to update initial store arises again
                        let loyalty =
                            checkoutModel.get('priceDetails')?.loyalty;
                        if (!loyaltyHelper.isBurnEligible(loyalty)) {
                            store.dispatch(setLoyaltyBurn(false));
                        } else if (
                            egtnlUtil.isVrboCkoOneKeySmallAmountSwitchOff() &&
                            loyalty?.redemptionValue
                                ?.pointsValueInMonetaryAmount < 1
                        ) {
                            // VRBOCXE-3994 Set to false by default when points value in currency is less than 1 (like 1USD)
                            store.dispatch(setLoyaltyBurn(false));
                        }

                        window.TC.apolloClient = initApollo(
                            checkoutModel.get('rawResponse')
                        );
                    }
                });
            }

            // Fetch data from /media endpoint
            window.TC.imagesModelFetch.then((response) => {
                if (!response) return;

                const media = {
                    images: response.images,
                    thumbnailUrl: response.thumbnailUrl,
                };

                store.dispatch(setMedia(media));
                setMediaState(media);
            });
        }, []);

        useEffect(() => {
            const renderPreloadedSection = () => {
                ReactDOM.render(
                    <PreloadedSection />,
                    document.getElementById('tc-preloaded-section')
                );
            };

            const renderContactDetailsStep = () => {
                const isMoveMessageHostModuleEnabled =
                    egtnlUtil.isMoveMessageHostModuleEnabled();
                if (ContactDetailsStepRef.current) {
                    ReactDOM.render(
                        <Provider store={store}>
                            <ContactDetailsStepRef.current
                                model={checkoutModelState}
                                store={store}
                                apolloClient={window.TC.apolloClient}
                                isMoveMessageHostModuleEnabled={
                                    isMoveMessageHostModuleEnabled
                                }
                            />
                        </Provider>,
                        document.getElementById('contact-details'),
                        () => {
                            PrimaryActionRenderedPromiseRef.current = true;
                        }
                    );
                }
            };

            const renderRulesStepTwo = () => {
                if (RulesStepTwoRef.current) {
                    ReactDOM.render(
                        <Provider store={store}>
                            <RulesStepTwoRef.current
                                model={checkoutModelState}
                                policies={
                                    checkoutModelState?.get('ces')?.policies
                                }
                            />
                        </Provider>,
                        document.getElementById('rules-and-policies'),
                        () => {
                            PrimaryActionRenderedPromiseRef.current = true;
                        }
                    );
                }
            };

            renderPreloadedSection();
            if (window.__INITIAL_STATE__.triggerInlineAjax) {
                if (egtnlUtil.isTwoPageCheckoutEnabled()) {
                    import(
                        '../components/rules-step/rules-step-2page-checkout'
                    ).then((module) => {
                        if (module && module.default) {
                            RulesStepTwoRef.current = module.default;
                            renderRulesStepTwo();
                        }
                    });
                } else {
                    import(
                        '../components/contact-details-step/contact-details-step'
                    ).then((module) => {
                        if (module && module.default) {
                            ContactDetailsStepRef.current = module.default;
                            renderContactDetailsStep();
                        }
                    });
                }
            } else {
                PrimaryActionRenderedPromiseRef.current = true;
            }

            onRender(PrimaryActionRenderedPromiseRef);
        }, [onRender, checkoutModelState, store]);

        return (
            <>
                {(mediaState || checkoutModelState?.get('listing')) && (
                    <ListingInfo
                        listing={checkoutModelState?.get('listing')}
                        media={mediaState}
                    />
                )}
            </>
        );
    };

    ReactDOM.render(
        <ListingInfoWrapper
            onRender={(PrimaryActionRenderedPromiseRef) => {
                // trigger PAR timings for OLB flow only
                if (
                    window.__INITIAL_STATE__.triggerInlineAjax &&
                    PrimaryActionRenderedPromiseRef.current
                ) {
                    setTimeout(() => {
                        if (ha.userTiming) {
                            ha.userTiming.mark('primary.action.rendered');
                        }
                    }, 750);
                }
            }}
        />,
        document.getElementById('rhub-listing-info')
    );
} else if (window.__INITIAL_STATE__.triggerInlineAjax) {
    window.TC.checkoutModelFetch.then((data) => {
        window.TC.checkoutModel = new CheckoutModel();
        const checkoutModel = window.TC.checkoutModel;
        const parsedData = checkoutModel.parse(data);
        // Set in baseController. If this gets executed before baseController, create the store
        const store = page.dataStorage.store || createStore(rootReducer);

        checkoutModel.set(parsedData);

        // update store initial state after the model data is in
        if (checkoutModel && store) {
            // update loyalty burn initial state
            // TODO: encapsulate better if the need to update initial store arises again
            let loyalty = checkoutModel.get('priceDetails')?.loyalty;
            if (!loyaltyHelper.isBurnEligible(loyalty)) {
                store.dispatch(setLoyaltyBurn(false));
            } else if (
                egtnlUtil.isVrboCkoOneKeySmallAmountSwitchOff() &&
                loyalty?.redemptionValue?.pointsValueInMonetaryAmount < 1
            ) {
                // VRBOCXE-3994 Set to false by default when points value in currency is less than 1 (like 1USD)
                store.dispatch(setLoyaltyBurn(false));
            }
            window.TC.apolloClient = initApollo(
                checkoutModel.get('rawResponse')
            );
        }

        // Don't render steps if there are checkout-service violations or if no listing data was provided
        // This check mimics the same check in checkoutController
        if (!checkoutModel.get('violations') && checkoutModel.get('listing')) {
            let PrimaryActionRenderedPromise;
            // Need to do the same for OLP in checkoutController
            ReactDOM.render(
                <PreloadedSection />,
                document.getElementById('tc-preloaded-section')
            );

            // OLB flow only do we render first step
            if (window.__INITIAL_STATE__.triggerInlineAjax) {
                if (egtnlUtil.isTwoPageCheckoutEnabled()) {
                    PrimaryActionRenderedPromise = new Promise((resolve) => {
                        import(
                            '../components/rules-step/rules-step-2page-checkout'
                        ).then((RulesStep2Step) => {
                            ReactDOM.render(
                                <Provider store={store}>
                                    <RulesStep2Step.default
                                        model={checkoutModel}
                                        policies={
                                            checkoutModel.get('ces')?.policies
                                        }
                                    />
                                </Provider>,
                                document.getElementById('rules-and-policies'),
                                resolve
                            );
                        });
                    });
                } else {
                    const isMoveMessageHostModuleEnabled =
                        egtnlUtil.isMoveMessageHostModuleEnabled();
                    PrimaryActionRenderedPromise = new Promise((resolve) => {
                        import(
                            '../components/contact-details-step/contact-details-step'
                        ).then((ContactDetailsStep) => {
                            ReactDOM.render(
                                <Provider store={store}>
                                    <ContactDetailsStep.default
                                        model={checkoutModel}
                                        store={store}
                                        isMoveMessageHostModuleEnabled={
                                            isMoveMessageHostModuleEnabled
                                        }
                                        apolloClient={window.TC.apolloClient}
                                    />
                                </Provider>,
                                document.getElementById('contact-details'),
                                resolve
                            );
                        });
                    });
                }
            } else {
                PrimaryActionRenderedPromise = Promise.resolve();
            }

            ReactDOM.render(
                <ListingInfo listing={checkoutModel.get('listing')} />,
                document.getElementById('rhub-listing-info'),
                () => {
                    // trigger PAR timings for OLB flow only
                    if (window.__INITIAL_STATE__.triggerInlineAjax) {
                        PrimaryActionRenderedPromise.then(() => {
                            setTimeout(() => {
                                if (ha.userTiming) {
                                    ha.userTiming.mark(
                                        'primary.action.rendered'
                                    );
                                }
                            }, 750);
                        });
                    }
                }
            );
        }

        return Promise.resolve();
    });
}

import(/* webpackChunkName: "checkout" */ './app');
